/**
 * GlobalView — NASA FIRMS Client
 *
 * Fetches active fire / hotspot data from NASA FIRMS
 * (Fire Information for Resource Management System).
 *
 * Uses the VIIRS SNPP NRT CSV endpoint (last 24 h, global).
 * Three MAP_KEY values are rotated round-robin to stay well
 * within the 5 000 transactions / 10 min rate limit.
 *
 * Maps CSV rows → Entity[] for the entity store.
 */

import type { Entity } from '@globalview/shared';

// ─── API Key Rotation ────────────────────────────────────────────

const FIRMS_KEYS: string[] = (
  import.meta.env.VITE_FIRMS_MAP_KEY as string ?? ''
)
  .split(',')
  .map((k) => k.trim())
  .filter(Boolean);

let keyIndex = 0;

function nextKey(): string {
  if (FIRMS_KEYS.length === 0) {
    console.error('[FIRMS] No API keys configured (VITE_FIRMS_MAP_KEY)');
    return '';
  }
  const key = FIRMS_KEYS[keyIndex % FIRMS_KEYS.length];
  keyIndex++;
  return key;
}

// ─── Endpoint ────────────────────────────────────────────────────

const FIRMS_BASE = 'https://firms.modaps.eosdis.nasa.gov/api/area/csv';
const SOURCE = 'VIIRS_SNPP_NRT';
const AREA = 'world';
const DAY_RANGE = '1'; // last 24 hours

function buildUrl(key: string): string {
  return `${FIRMS_BASE}/${key}/${SOURCE}/${AREA}/${DAY_RANGE}`;
}

// ─── CSV Parsing ─────────────────────────────────────────────────

interface FirmsRow {
  latitude: number;
  longitude: number;
  bright_ti4: number;
  scan: number;
  track: number;
  acq_date: string;
  acq_time: string;
  satellite: string;
  instrument: string;
  confidence: string;   // 'h', 'n', 'l'
  version: string;
  bright_ti5: number;
  frp: number;
  daynight: string;     // 'D' or 'N'
}

function parseCSV(csv: string): FirmsRow[] {
  const lines = csv.trim().split('\n');
  if (lines.length < 2) return [];

  // First line is the header
  const header = lines[0].split(',');
  const latIdx = header.indexOf('latitude');
  const lonIdx = header.indexOf('longitude');
  const bri4Idx = header.indexOf('bright_ti4');
  const scanIdx = header.indexOf('scan');
  const trackIdx = header.indexOf('track');
  const dateIdx = header.indexOf('acq_date');
  const timeIdx = header.indexOf('acq_time');
  const satIdx = header.indexOf('satellite');
  const instrIdx = header.indexOf('instrument');
  const confIdx = header.indexOf('confidence');
  const verIdx = header.indexOf('version');
  const bri5Idx = header.indexOf('bright_ti5');
  const frpIdx = header.indexOf('frp');
  const dnIdx = header.indexOf('daynight');

  if (latIdx === -1 || lonIdx === -1) {
    console.error('[FIRMS] CSV header missing latitude/longitude columns');
    return [];
  }

  const rows: FirmsRow[] = [];

  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(',');
    if (cols.length < header.length) continue;

    const lat = parseFloat(cols[latIdx]);
    const lon = parseFloat(cols[lonIdx]);
    if (isNaN(lat) || isNaN(lon)) continue;

    rows.push({
      latitude: lat,
      longitude: lon,
      bright_ti4: parseFloat(cols[bri4Idx]) || 0,
      scan: parseFloat(cols[scanIdx]) || 0,
      track: parseFloat(cols[trackIdx]) || 0,
      acq_date: cols[dateIdx] ?? '',
      acq_time: cols[timeIdx] ?? '',
      satellite: cols[satIdx] ?? '',
      instrument: cols[instrIdx] ?? '',
      confidence: (cols[confIdx] ?? 'l').toLowerCase(),
      version: cols[verIdx] ?? '',
      bright_ti5: parseFloat(cols[bri5Idx]) || 0,
      frp: parseFloat(cols[frpIdx]) || 0,
      daynight: cols[dnIdx] ?? '',
    });
  }

  return rows;
}

// ─── Mapping Helpers ─────────────────────────────────────────────

/**
 * Map confidence level → CSS color.
 * High-confidence fires are vivid red/crimson, nominal → orange, low → yellow.
 */
export function confidenceToColor(confidence: string): string {
  switch (confidence) {
    case 'h': return '#ff2200';   // high — bright red
    case 'n': return '#ff8800';   // nominal — orange
    case 'l': return '#ffcc00';   // low — amber/yellow
    default:  return '#ff8800';
  }
}

/**
 * Map FRP (fire radiative power, MW) → point size multiplier.
 * Log scale so small fires are still visible while large ones stand out.
 */
export function frpToSize(frp: number): number {
  if (frp <= 0) return 1;
  // size ≈ 1 + log2(frp+1) → 0 MW=1, 1 MW=1.6, 10 MW=3.5, 100 MW=6.7
  return 1 + Math.log2(frp + 1) * 0.85;
}

// ─── Row → Entity ────────────────────────────────────────────────

function rowToEntity(row: FirmsRow, _index: number): Entity {
  const color = confidenceToColor(row.confidence);

  // Build a deterministic ID from position + date + time
  const id = `firms-${row.latitude.toFixed(5)}-${row.longitude.toFixed(5)}-${row.acq_date}-${row.acq_time}`;

  // Build timestamp from acq_date + acq_time (e.g. "2026-03-13" + "1345")
  let timestamp: string;
  try {
    const timeStr = String(row.acq_time).padStart(4, '0');
    const h = timeStr.slice(0, 2);
    const m = timeStr.slice(2, 4);
    timestamp = `${row.acq_date}T${h}:${m}:00Z`;
  } catch {
    timestamp = new Date().toISOString();
  }

  return {
    id,
    type: 'wildfire',
    source: 'firms',
    position: {
      lat: row.latitude,
      lon: row.longitude,
      alt: 0,
    },
    heading: undefined,
    timestamp,
    style: {
      color,
      size: frpToSize(row.frp),
    },
    properties: {
      brightness: row.bright_ti4,
      frp: row.frp,
      confidence: row.confidence,
      satellite: row.satellite,
      instrument: row.instrument,
      daynight: row.daynight,
      acqDate: row.acq_date,
      acqTime: String(row.acq_time).padStart(4, '0'),
      scan: row.scan,
      track: row.track,
    },
    confidence: row.confidence === 'h' ? 1.0 : row.confidence === 'n' ? 0.7 : 0.4,
  };
}

// ─── Fetch ───────────────────────────────────────────────────────

/**
 * Fetch active fire data from NASA FIRMS.
 * Returns Entity[] mapped from VIIRS SNPP NRT CSV.
 */
export async function fetchActiveFires(): Promise<Entity[]> {
  const key = nextKey();
  if (!key) return [];

  const url = buildUrl(key);
  console.log('[FIRMS] Fetching active fires...');

  try {
    const res = await fetch(url);
    if (!res.ok) {
      console.error(`[FIRMS] HTTP ${res.status} for ${url}`);
      return [];
    }

    const csv = await res.text();
    const rows = parseCSV(csv);

    if (rows.length === 0) {
      console.warn('[FIRMS] No fire data returned');
      return [];
    }

    const entities = rows.map(rowToEntity);
    console.log(`[FIRMS] Parsed ${entities.length} fire detections`);
    return entities;
  } catch (err) {
    console.error('[FIRMS] Fetch failed:', err);
    return [];
  }
}
